/**
 * ActionButtons Component
 * 複製文案和下載圖片按鈕
 * Requirements: 8.1, 8.2, 8.3, 9.1, 9.2
 */

import { useState, useCallback, type RefObject } from 'react';
import { ClipboardDocumentIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { CardData, CalculationResult } from '../types/card';
import type { Translations } from '../i18n';
import { generateTradeText } from '../utils/textGenerator';
import { downloadCardImage } from '../utils/imageExporter';

interface ActionButtonsProps {
  cardData: CardData;
  result: CalculationResult;
  cardRef: RefObject<HTMLDivElement | null>;
  t: Translations;
}

type ToastType = 'success' | 'error' | null;

export function ActionButtons({ cardData, result, cardRef, t }: ActionButtonsProps) {
  const [toast, setToast] = useState<{ type: ToastType; message: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // 顯示提示訊息
  const showToast = useCallback((type: ToastType, message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // 複製文案到剪貼簿
  const handleCopyText = useCallback(async () => {
    try {
      const text = generateTradeText(cardData, result);
      await navigator.clipboard.writeText(text);
      showToast('success', t.copied);
    } catch (error) {
      console.error('Failed to copy text:', error);
      showToast('error', 'Copy failed');
    }
  }, [cardData, result, showToast, t]);

  // 下載卡片圖片
  const handleDownloadImage = useCallback(async () => {
    if (!cardRef.current) {
      showToast('error', 'Cannot find card element');
      return;
    }

    setIsExporting(true);
    try {
      await downloadCardImage(cardRef.current, cardData.productName);
      showToast('success', 'Image downloaded!');
    } catch (error) {
      console.error('Failed to download image:', error);
      showToast('error', 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [cardRef, cardData.productName, showToast]);

  return (
    <div className="relative">
      {/* 按鈕組 */}
      <div className="flex gap-3">
        <button
          onClick={handleCopyText}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <ClipboardDocumentIcon className="w-5 h-5" />
          {t.copyText}
        </button>

        <button
          onClick={handleDownloadImage}
          disabled={isExporting}
          className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <>
              <svg
                className="w-5 h-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t.downloading}
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="w-5 h-5" />
              {t.downloadImage}
            </>
          )}
        </button>
      </div>

      {/* Toast 提示 */}
      {toast && (
        <div
          className={`absolute top-full left-0 right-0 mt-3 px-4 py-3 rounded-lg text-center text-sm font-medium transition-all ${
            toast.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default ActionButtons;
